import React, { useEffect, useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Bookmark,
  ChevronLeft,
  Compass,
  Heart,
  Loader2,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  MessageCircle,
  RefreshCw,
  Route,
  Star,
  UserRound,
} from "lucide-react";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";

import Navbar from "./Navbar";
import Footer from "./Footer";
import NoImg from "../assets/images/no-image-icon-23485.png";
import DefaultUserAvatar from "../assets/images/Frame 440.png";
import starGreen from "../assets/icons/StarGreen.svg";
import useUserStore from "../store/userStore";
import { useFavorites, useRemoveFavorite } from "../hooks/useFavorites";
import type { Favorite } from "../types/favorite";

type ProfileTab = "overview" | "reviews" | "saved";

interface UserReview {
  review_id: string;
  place: string;
  place_name?: string;
  user: string;
  rate: number;
  comment: string;
  date: string;
}

interface FavoritePlaceDetails {
  place_id: string;
  name: string;
  images?: Array<{ file_url: string }>;
  address?: string;
  cities?: Array<{ id: number; name: string }>;
  city?: string | { name?: string };
  description?: string;
  rate?: number | null;
  avg_rating?: number | null;
  review_count?: number;
}

interface FavoritePlanDetails {
  id: number;
  name: string;
  description?: string;
  steps?: Array<{
    place: string;
    place_name?: string;
    first_image_url?: string | null;
  }>;
}

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fetchFavoritePlace = async (placeId: string): Promise<FavoritePlaceDetails> => {
  const response = await fetch(`${BASE_URL}/api/places/${placeId}/`);
  if (!response.ok) throw new Error("دریافت مکان ناموفق بود");
  const body = await response.json();
  return body.data ?? body;
};

const fetchFavoritePlan = async (planId: string): Promise<FavoritePlanDetails> => {
  const response = await fetch(`${BASE_URL}/api/plans/${planId}/`);
  if (!response.ok) throw new Error("دریافت برنامه سفر ناموفق بود");
  const body = await response.json();
  return body.data ?? body;
};

const resolveImageUrl = (fileUrl?: string | null) => {
  if (!fileUrl) return NoImg;
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  return `${BASE_URL ?? ""}${fileUrl}`;
};

const fetchUserReviews = async (userId: string): Promise<UserReview[]> => {
  const params = new URLSearchParams({ user_id: userId, page_size: "50" });
  const response = await fetch(`${BASE_URL}/api/reviews/?${params.toString()}`, {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!response.ok) throw new Error("دریافت دیدگاه‌ها ناموفق بود");
  const responseText = await response.text();
  if (!responseText.trim()) throw new Error("پاسخ دیدگاه‌ها خالی است");

  let body: any;
  try {
    body = JSON.parse(responseText);
  } catch {
    throw new Error("پاسخ دیدگاه‌ها معتبر نیست");
  }
  const collection = Array.isArray(body)
    ? body
    : body.results ?? body.data?.results ?? body.data ?? [];

  return Array.isArray(collection) ? collection : [];
};

const getFavoriteType = (favorite: Favorite) =>
  favorite.model_type ?? favorite.item_type ?? "place";

const getPlaceCity = (place?: FavoritePlaceDetails) =>
  place?.cities?.[0]?.name ||
  (typeof place?.city === "string" ? place.city : place?.city?.name) ||
  "";

const formatDate = (date?: string) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("fa-IR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(parsed);
};

const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string;
  action: string;
}> = ({ icon, title, description, link, action }) => (
  <div className="rounded-lg border border-dashed border-gray-300 bg-white px-5 py-10 text-center">
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#cffae7] text-[#038545]">
      {icon}
    </div>
    <h3 className="mt-4 font-myYekanDemibold text-gray-900">{title}</h3>
    <p className="mx-auto mt-2 max-w-md text-sm leading-7 text-gray-500">{description}</p>
    <Link to={link} className="mt-5 inline-flex h-10 items-center justify-center rounded-full bg-[#038545] px-5 text-sm text-white transition hover:bg-[#068646]">
      {action}
    </Link>
  </div>
);

const ReviewCard: React.FC<{
  review: UserReview;
  userName: string;
  avatarUrl: string | null;
}> = ({ review, userName, avatarUrl }) => (
  <article className="rounded-lg border border-gray-200 bg-white p-4 transition hover:shadow-md">
    <div className="min-w-0">
      <span className="text-[11px] text-gray-400">دیدگاه درباره</span>
      <Link to={`/places/${review.place}`} className="mt-1 block truncate font-myYekanDemibold text-gray-900 hover:text-[#038545]">
        {review.place_name || "مشاهده مکان"}
      </Link>
    </div>
    <p className="mt-3 line-clamp-3 text-justify text-sm leading-7 text-gray-700">{review.comment}</p>
    <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
      <div className="flex min-w-0 items-center gap-2">
        <img src={avatarUrl || DefaultUserAvatar} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
        <div className="flex min-w-0 items-center gap-2">
          <span className="max-w-[90px] truncate text-xs font-myYekanFaNumDemiBold text-[#222222]">{userName}</span>
          {formatDate(review.date) && <time className="shrink-0 text-[10px] text-gray-400">{formatDate(review.date)}</time>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5" dir="ltr" aria-label={`امتیاز ${review.rate} از ۵`}>
        {Array.from({ length: 5 }, (_, index) => (
          <Star key={index} className={`h-3.5 w-3.5 ${index < Math.round(review.rate) ? "fill-[#038545] text-[#038545]" : "text-gray-300"}`} aria-hidden="true" />
        ))}
      </div>
    </div>
  </article>
);

const ProfileFavoriteCards: React.FC<{ favorites: Favorite[]; userId: string }> = ({ favorites, userId }) => {
  const removeFavorite = useRemoveFavorite(userId);
  const placeFavorites = favorites.filter((favorite) => getFavoriteType(favorite) === "place");
  const planFavorites = favorites.filter((favorite) => getFavoriteType(favorite) === "plan");
  const placeQueries = useQueries({
    queries: placeFavorites.map((favorite) => ({
      queryKey: ["place", favorite.object_id],
      queryFn: () => fetchFavoritePlace(favorite.object_id),
      staleTime: 10 * 60 * 1000,
    })),
  });
  const planQueries = useQueries({
    queries: planFavorites.map((favorite) => ({
      queryKey: ["plan", favorite.object_id],
      queryFn: () => fetchFavoritePlan(favorite.object_id),
      staleTime: 10 * 60 * 1000,
    })),
  });
  const planFirstSteps = planQueries.map((query) => query.data?.steps?.[0]);
  const planPlaceQueries = useQueries({
    queries: planFirstSteps.map((step) => ({
      queryKey: ["place", step?.place],
      queryFn: () => fetchFavoritePlace(step!.place),
      enabled: Boolean(step?.place) && !step?.first_image_url,
      staleTime: 10 * 60 * 1000,
    })),
  });
  const placeDetails = new Map(placeFavorites.map((favorite, index) => [favorite.object_id, placeQueries[index]?.data]));
  const planDetails = new Map(planFavorites.map((favorite, index) => [favorite.object_id, planQueries[index]?.data]));
  const planFallbackPlaces = new Map(
    planFavorites.map((favorite, index) => [favorite.object_id, planPlaceQueries[index]?.data]),
  );
  const loading = new Map([
    ...placeFavorites.map((favorite, index) => [favorite.object_id, placeQueries[index]?.isLoading ?? false] as const),
    ...planFavorites.map((favorite, index) => [
      favorite.object_id,
      (planQueries[index]?.isLoading ?? false) || (planPlaceQueries[index]?.isLoading ?? false),
    ] as const),
  ]);

  const handleRemove = async (favorite: Favorite) => {
    try {
      await removeFavorite.mutateAsync(favorite);
      toast.success("از علاقه‌مندی‌ها حذف شد", { className: "toast", hideProgressBar: true });
    } catch {
      toast.error("حذف از علاقه‌مندی‌ها ناموفق بود", { className: "toast", hideProgressBar: true });
    }
  };

  return (
    <div className="grid grid-cols-1 gap-5 tablet:grid-cols-2 desktop:grid-cols-3">
      {favorites.map((favorite) => {
        const isPlan = getFavoriteType(favorite) === "plan";
        const place = placeDetails.get(favorite.object_id);
        const plan = planDetails.get(favorite.object_id);
        const firstStep = plan?.steps?.[0];
        const planFallbackPlace = planFallbackPlaces.get(favorite.object_id);
        const rating = place?.avg_rating ?? place?.rate ?? 0;
        const reviewCount = place?.review_count ?? 0;
        const title = favorite.item_name || plan?.name || place?.name || "بدون نام";
        const description = isPlan ? plan?.description : place?.description;
        const location = isPlan ? firstStep?.place_name : getPlaceCity(place);
        const image = isPlan
          ? resolveImageUrl(firstStep?.first_image_url || planFallbackPlace?.images?.[0]?.file_url)
          : resolveImageUrl(place?.images?.[0]?.file_url);
        const isRemoving = removeFavorite.isPending && removeFavorite.variables?.id === favorite.id;

        return (
          <article key={favorite.id} className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white transition hover:shadow-md">
            <button
              type="button"
              onClick={() => void handleRemove(favorite)}
              disabled={removeFavorite.isPending}
              aria-label={`حذف ${title} از علاقه‌مندی‌ها`}
              className="absolute left-2 top-2 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-[#E5484D] shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
            >
              {isRemoving ? <Loader2 size={18} className="animate-spin" /> : <Heart size={18} fill="currentColor" />}
            </button>
            <Link to={isPlan ? `/plans/${favorite.object_id}` : `/places/${favorite.object_id}`} className="block h-full">
              <div className="relative h-48 overflow-hidden bg-gray-200">
                {loading.get(favorite.object_id) ? (
                  <div className="h-full w-full animate-pulse bg-gray-200" />
                ) : (
                  <img src={image} alt={title} onError={(event) => { event.currentTarget.src = NoImg; }} className="h-full w-full object-cover transition duration-300 group-hover:brightness-75" />
                )}
                <span className="absolute right-2 top-2 rounded-md bg-white/90 px-2 py-1 text-[10px] text-gray-700 shadow-sm backdrop-blur-sm font-myYekanFaNumMedium">{isPlan ? "برنامه سفر" : "مکان"}</span>
              </div>
              <div className="p-3 text-black">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="min-w-0 flex-1 line-clamp-1 text-sm font-myYekanFaNumDemiBold">{title}</h3>
                  {isPlan ? (
                    <span className="shrink-0 text-xs text-gray-500">({plan?.steps?.length ?? 0} مقصد)</span>
                  ) : rating > 0 ? (
                    <span className="flex shrink-0 items-center gap-1 text-sm text-[#038545]">{Number(rating.toFixed(1))}<img src={starGreen} className="mb-1 h-3 w-3" alt="امتیاز" /></span>
                  ) : null}
                </div>
                {(location || (!isPlan && reviewCount > 0)) && (
                  <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-gray-800">
                    <div className="flex min-w-0 items-center"><MapPin size={12} className="ml-1 shrink-0" /><span className="line-clamp-1">{location || ""}</span></div>
                    {!isPlan && reviewCount > 0 && <span className="shrink-0 text-gray-500">({reviewCount} نظر)</span>}
                  </div>
                )}
                {description && <div className="mt-2 line-clamp-2 text-justify text-[10px] text-gray-500"><ReactMarkdown>{description}</ReactMarkdown></div>}
              </div>
            </Link>
          </article>
        );
      })}
    </div>
  );
};

const UserProfile: React.FC = () => {
  const { user, clearUser } = useUserStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<ProfileTab>(
    requestedTab === "reviews" || requestedTab === "saved" ? requestedTab : "overview",
  );

  useEffect(() => {
    setActiveTab(requestedTab === "reviews" || requestedTab === "saved" ? requestedTab : "overview");
  }, [requestedTab]);
  const {
    data: favorites = [],
    isLoading: favoritesLoading,
    isError: favoritesError,
    refetch: refetchFavorites,
  } = useFavorites(user?.userId);
  const {
    data: reviews = [],
    isLoading: reviewsLoading,
    isError: reviewsError,
    refetch: refetchReviews,
  } = useQuery({
    queryKey: ["profile-reviews-v3", user?.userId],
    queryFn: () => fetchUserReviews(user!.userId),
    enabled: Boolean(user?.userId),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const counts = useMemo(
    () => ({
      reviews: reviews.length,
      places: favorites.filter((favorite) => getFavoriteType(favorite) === "place").length,
      plans: favorites.filter((favorite) => getFavoriteType(favorite) === "plan").length,
    }),
    [favorites, reviews],
  );

  const displayName = user?.fullName || user?.username || "کاربر لوتکا";
  const initial = displayName.trim().charAt(0) || "ل";
  const avatarUrl = user?.image
    ? /^https?:\/\//i.test(user.image)
      ? user.image
      : `${BASE_URL ?? ""}${user.image}`
    : null;

  const handleLogout = () => {
    clearUser();
    toast.success("با موفقیت خارج شدید", { className: "toast", hideProgressBar: true });
    navigate("/");
  };

  const selectTab = (tab: ProfileTab) => {
    setActiveTab(tab);
    if (tab === "overview") {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col font-myYekanFaNumRegular">
        <Navbar />
        <main className="site-container flex flex-1 items-center justify-center pb-16 pt-20 tablet:py-16">
          <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-8 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#f3fefa] text-[#038545]"><UserRound size={30} /></div>
            <h1 className="mt-5 text-xl font-myYekanFaNumDemiBold text-gray-950">پروفایل سفر شما</h1>
            <p className="mt-3 text-sm leading-7 text-gray-600">برای دیدن دیدگاه‌ها، علاقه‌مندی‌ها و برنامه‌های ذخیره‌شده وارد حساب لوتکا شوید.</p>
            <Link to="/login" className="mt-6 inline-flex h-11 items-center gap-2 rounded-full bg-[#038545] px-6 text-sm text-white hover:bg-[#068646]"><LogIn size={17} /> ورود به حساب</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const tabs: Array<{ id: ProfileTab; label: string; count?: number }> = [
    { id: "overview", label: "نمای کلی" },
    { id: "reviews", label: "دیدگاه‌ها", count: counts.reviews },
    { id: "saved", label: "ذخیره‌شده‌ها", count: favorites.length },
  ];

  return (
    <div className="flex min-h-screen flex-col  font-myYekanFaNumRegular" dir="rtl">
      <Navbar />
      <main className="site-container flex-1 pb-6 pt-20 tablet:py-8 desktop:py-10">
        <div className="mx-auto max-w-6xl">
          <section className="rounded-lg border border-gray-200 bg-white p-4 tablet:p-6">
            <div className="flex flex-col gap-4 tablet:flex-row tablet:items-center tablet:justify-between">
              <div className="flex min-w-0 items-center gap-3 tablet:gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#f3fefa] text-xl text-[#068646] tablet:h-24 tablet:w-24 tablet:text-3xl">
                  {avatarUrl ? <img src={avatarUrl} alt={displayName} className="h-full w-full object-cover" /> : <span className="font-myYekanFaNumDemiBold">{initial}</span>}
                </div>
                <div className="min-w-0">
                  <h1 className="truncate text-lg font-myYekanFaNumDemiBold text-gray-950 tablet:text-2xl">{displayName}</h1>
                  <div className="mt-1 flex min-w-0 flex-col items-start gap-1 text-xs text-gray-500 tablet:flex-row tablet:flex-wrap tablet:items-center tablet:gap-x-3">
                    <span>@{user.username}</span>
                    {user.email && <span className="flex min-w-0 max-w-full items-center gap-1"><Mail size={13} className="shrink-0" /><span className="truncate">{user.email}</span></span>}
                  </div>
                </div>
              </div>
              <button onClick={handleLogout} className="inline-flex h-10 w-full items-center justify-center gap-2 self-start rounded-xl border border-gray-300 bg-white px-4 text-xs text-gray-700 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 tablet:w-auto tablet:self-auto">
                <LogOut size={16} /> خروج از حساب
              </button>
            </div>
          </section>

          <nav className="mt-6 flex gap-1 overflow-x-auto border-b border-gray-200" aria-label="بخش‌های پروفایل">
            {tabs.map((tab) => (
              <button key={tab.id} type="button" onClick={() => selectTab(tab.id)} className={`shrink-0 border-b-2 px-4 py-3 text-sm transition ${activeTab === tab.id ? "border-[#038545] font-myYekanDemibold text-gray-950" : "border-transparent text-gray-500 hover:text-gray-800"}`}>
                {tab.label}{typeof tab.count === "number" && <span className="mr-1 text-xs text-gray-400">({tab.count})</span>}
              </button>
            ))}
          </nav>

          <div className="mt-6">
            {activeTab === "overview" && (
              <div className="grid gap-6 desktop:grid-cols-[minmax(0,1fr)_288px]">
                <section>
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-myYekanDemibold text-[#222222] tablet:text-xl desktop:text-2xl">آخرین دیدگاه‌ها</h2>
                    {reviews.length > 0 && <button onClick={() => selectTab("reviews")} className="flex items-center gap-1 text-xs text-gray-700 hover:text-[#038545]">مشاهده همه <ChevronLeft size={15} /></button>}
                  </div>
                  {reviewsLoading ? (
                    <div className="space-y-3">{[0, 1, 2].map((item) => <div key={item} className="h-32 animate-pulse rounded-lg border border-gray-200 bg-white p-4"><div className="h-4 w-1/3 rounded bg-gray-200" /><div className="mt-5 h-3 w-full rounded bg-gray-200" /><div className="mt-2 h-3 w-2/3 rounded bg-gray-200" /></div>)}</div>
                  ) : reviewsError ? (
                    <button onClick={() => void refetchReviews()} className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700"><RefreshCw size={16} /> تلاش دوباره برای دریافت دیدگاه‌ها</button>
                  ) : reviews.length ? (
                    <div className="space-y-3">{reviews.slice(0, 3).map((review) => <ReviewCard key={review.review_id} review={review} userName={displayName} avatarUrl={avatarUrl} />)}</div>
                  ) : (
                    <EmptyState icon={<MessageCircle size={22} />} title="هنوز دیدگاهی ثبت نکرده‌اید" description="تجربه‌تان از مکان‌هایی که دیده‌اید می‌تواند راهنمای سفر دیگران باشد." link="/destinations" action="پیدا کردن مکان" />
                  )}
                </section>

                <aside className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <h2 className="font-myYekanDemibold text-[#222222]">فعالیت شما</h2>
                    <div className="mt-4 divide-y divide-gray-100 text-sm">
                      <button type="button" onClick={() => selectTab("reviews")} className="flex w-full items-center justify-between py-3 text-gray-700 hover:text-[#038545]"><span className="flex items-center gap-2"><MessageCircle size={17} /> دیدگاه‌ها</span><span className="font-myYekanFaNumMedium">{counts.reviews}</span></button>
                      <button type="button" onClick={() => selectTab("saved")} className="flex w-full items-center justify-between py-3 text-gray-700 hover:text-[#038545]"><span className="flex items-center gap-2"><Heart size={17} /> مکان‌های محبوب</span><span className="font-myYekanFaNumMedium">{counts.places}</span></button>
                      <button type="button" onClick={() => selectTab("saved")} className="flex w-full items-center justify-between py-3 text-gray-700 hover:text-[#038545]"><span className="flex items-center gap-2"><Route size={17} /> برنامه‌ها</span><span className="font-myYekanFaNumMedium">{counts.plans}</span></button>
                    </div>
                  </div>
                  <Link to="/planner" className="group block rounded-2xl bg-[#068646] p-5 text-white transition hover:bg-[#2e855b]">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-[#cffae7]">
                      <Compass size={23} />
                    </div>
                    <h2 className="mt-4 font-myYekanDemibold">سفر بعدی‌تان را بسازید</h2>
                    <p className="mt-2 text-xs leading-6 text-white/70">با برنامه‌ریز لوتکا یک مسیر متناسب با سلیقه‌تان بگیرید.</p>
                    <span className="mt-4 inline-flex h-10 items-center gap-1 rounded-full bg-white px-4 text-xs font-myYekanMedium text-[#068646] transition group-hover:bg-[#f3fefa]">
                      شروع برنامه‌ریزی
                      <ChevronLeft size={15} className="transition group-hover:-translate-x-1" />
                    </span>
                  </Link>
                </aside>
              </div>
            )}

            {activeTab === "reviews" && (
              <section>
                <div className="mb-4 flex items-end justify-between"><h2 className="text-lg font-myYekanDemibold text-[#222222] tablet:text-xl desktop:text-2xl">همه دیدگاه‌ها</h2><span className="text-xs text-gray-500">{counts.reviews} دیدگاه</span></div>
                {reviewsLoading ? <div className="grid gap-3 desktop:grid-cols-2">{[0, 1, 2, 3].map((item) => <div key={item} className="h-32 animate-pulse rounded-lg border border-gray-200 bg-white p-4"><div className="h-4 w-1/3 rounded bg-gray-200" /><div className="mt-5 h-3 w-full rounded bg-gray-200" /><div className="mt-2 h-3 w-2/3 rounded bg-gray-200" /></div>)}</div> : reviewsError ? <button onClick={() => void refetchReviews()} className="flex h-11 items-center gap-2 rounded-xl border border-red-200 bg-white px-4 text-sm text-red-600 hover:bg-red-50"><RefreshCw size={16} /> تلاش دوباره</button> : reviews.length ? <div className="grid gap-3 desktop:grid-cols-2">{reviews.map((review) => <ReviewCard key={review.review_id} review={review} userName={displayName} avatarUrl={avatarUrl} />)}</div> : <EmptyState icon={<MessageCircle size={22} />} title="دیدگاهی وجود ندارد" description="اولین تجربه سفر خود را با جامعه لوتکا به اشتراک بگذارید." link="/destinations" action="دیدن مقصدها" />}
              </section>
            )}

            {activeTab === "saved" && (
              <section>
                <div className="mb-4 flex items-end justify-between"><h2 className="text-lg font-myYekanDemibold text-[#222222] tablet:text-xl desktop:text-2xl">ذخیره‌شده‌ها</h2><span className="text-xs text-gray-500">{favorites.length} مورد</span></div>
                {favoritesLoading ? <div className="grid grid-cols-1 gap-5 tablet:grid-cols-2 desktop:grid-cols-3">{[0, 1, 2].map((item) => <div key={item} className="overflow-hidden rounded-lg border border-gray-200 bg-white"><div className="h-48 animate-pulse bg-gray-200" /><div className="space-y-3 p-3"><div className="h-4 w-1/2 rounded bg-gray-200" /><div className="h-3 w-3/4 rounded bg-gray-200" /></div></div>)}</div> : favoritesError ? <button onClick={() => void refetchFavorites()} className="flex h-11 items-center gap-2 rounded-xl border border-red-200 bg-white px-4 text-sm text-red-600 hover:bg-red-50"><RefreshCw size={16} /> تلاش دوباره</button> : favorites.length ? <ProfileFavoriteCards favorites={favorites} userId={user.userId} /> : <EmptyState icon={<Bookmark size={22} />} title="هنوز چیزی ذخیره نکرده‌اید" description="مکان‌ها و برنامه‌های مورد علاقه را ذخیره کنید تا همیشه به آن‌ها دسترسی داشته باشید." link="/destinations" action="کاوش مقصدها" />}
              </section>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserProfile;
